<script setup lang="ts">
import {
  GpAvatar,
  GpBtn,
  GpMenu,
  GpMenuItemBtn,
  GpMenuItemGroupHeader,
  GpMenuItemStatic,
  GpTag,
  GpTooltip,
} from '@gportal/uikit';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { computed, ref } from 'vue';
import ImpersonatingTag from './ImpersonatingTag.vue';

const props = withDefaults(
  defineProps<{
    avatarUrl: string;
    fullName?: string;
    username: string;
    email: string;

    isAdmin?: boolean;
    isImpersonating?: boolean;
    hasWarning?: boolean;
  }>(),
  {
    fullName: undefined,
    isAdmin: false,
    hasWarning: false,
    isImpersonating: false,
  },
);

const emit = defineEmits<{
  (e: 'logout'): void;
}>();

const userHasName = computed(() => {
  return (
    (props.fullName != null && props.fullName.length > 0) ||
    (props.username != null && props.username.length > 0)
  );
});

const userMenu = ref<InstanceType<typeof GpMenu>>();
defineExpose({
  showMenu: () => {
    userMenu.value.showMenuBody();
  },
  hideMenu: () => {
    userMenu.value.hideMenuBody();
  },
});
</script>

<template>
  <GpMenu btn-type="flat" size="sm" body-min-width="20rem" ref="userMenu">
    <template #toggle="{ toggle, isOpened }">
      <GpBtn
        :btn-type="hasWarning || isImpersonating ? 'warning' : 'transparent'"
        size="sm"
        class="tw-flex tw-items-center tw-gap-2"
        @click="toggle"
      >
        <GpAvatar
          :src="avatarUrl"
          :alt="username"
          rounded
          custom-size
          style="--gp-avatar-size: 20px"
        />
        <div
          class="tw-hidden tw-flex-1 tw-overflow-hidden tw-text-ellipsis tw-leading-none sm:tw-block"
        >
          <span>
            {{ fullName || email }}
          </span>
        </div>

        <template #end>
          <FontAwesomeIcon
            :icon="['fas', 'fa-chevron-up']"
            class="tw-block tw-transition-transform"
            :class="{
              'tw-rotate-180': !isOpened,
              'tw-rotate-0': isOpened,
            }"
          />
        </template>
      </GpBtn>
    </template>

    <template #default>
      <GpMenuItemGroupHeader label="User" dense />
      <GpMenuItemStatic v-slot="{ contentClass }">
        <div :class="contentClass">
          <div class="tw-flex tw-items-center tw-gap-4 tw-py-2">
            <GpTooltip
              v-if="isImpersonating"
              content="You are impersonating this user"
              class="tw-relative"
            >
              <GpAvatar
                :src="avatarUrl"
                :alt="username"
                rounded
                custom-size
                style="--gp-avatar-size: 40px"
              />
              <ImpersonatingTag
                v-if="isImpersonating"
                class="tw-absolute -tw-bottom-1 -tw-right-1"
              />
            </GpTooltip>

            <GpAvatar
              v-else
              :src="avatarUrl"
              :alt="username"
              rounded
              custom-size
              style="--gp-avatar-size: 40px"
            />
            <div class="tw-flex-1 tw-text-ellipsis tw-leading-none">
              <p v-if="userHasName">
                <template v-if="fullName != null && fullName.length > 0">
                  {{ fullName }}
                </template>
                <template v-else-if="username != null && username.length > 0">
                  {{ username }}
                </template>
                <template class="tw-text-xs">
                  {{ email }}
                </template>
                <GpTag
                  v-if="isAdmin"
                  outline
                  size="xs"
                  label="Admin"
                  class="tw-ml-2 tw-uppercase"
                />
              </p>
              <p v-else>
                {{ email }}
                <GpTag
                  v-if="isAdmin"
                  outline
                  size="xs"
                  label="Admin"
                  class="tw-ml-2 tw-uppercase"
                />
              </p>
            </div>
          </div>
        </div>
      </GpMenuItemStatic>

      <!-- @slot other menu items -->
      <slot></slot>
      <GpMenuItemBtn @click="emit('logout')" label="Sign out" :icon="['fas', 'fa-sign-out-alt']" />
    </template>
  </GpMenu>
</template>

<style scoped></style>
